/**
 * Chemins du module de prospection, en un seul endroit : les liens internes du module
 * passent par ces fonctions (les anciennes adresses sont redirigées par le routeur).
 */
const ROOT = '/super-admin/crm/prospection';

export const PROSPECTING_PATHS = {
    today: ROOT,
    sends: `${ROOT}/envois`,
    hotels: `${ROOT}/hotels`,
    replies: `${ROOT}/reponses`,
    review: `${ROOT}/relecture`,
    campaigns: `${ROOT}/campagnes`,
    settings: `${ROOT}/reglages`,
    mailboxes: `${ROOT}/reglages/boites`,
    import: `${ROOT}/reglages/import`,
    recap: `${ROOT}/reglages/bilan`,
    workshop: `${ROOT}/atelier`,
    templates: `${ROOT}/trames`,
} as const;

export type CampaignTab = 'apercu' | 'trames' | 'cibles' | 'statistiques' | 'reglages';

export const CAMPAIGN_TABS: CampaignTab[] = ['apercu', 'trames', 'cibles', 'statistiques', 'reglages'];

/** Sections des Réglages de campagne qu'une adresse peut viser (`#periodes`, `#parametres`, `#arguments`). */
export type SettingsAnchor = 'periodes' | 'parametres' | 'arguments';

/** Anciens onglets Périodes et Paramètres : regroupés dans Réglages, ils y deviennent des ancres. `arguments` n'a jamais été un onglet. */
const LEGACY_SETTINGS_TABS: readonly SettingsAnchor[] = ['periodes', 'parametres'];

/** Vue d'ensemble : checklist de mise en route et entonnoir. */
export const DEFAULT_CAMPAIGN_TAB: CampaignTab = 'apercu';

export function campaignTabFromSlug(slug: string | null | undefined): CampaignTab | null {
    return CAMPAIGN_TABS.find((tab) => tab === slug) ?? null;
}

/** Ancien onglet `periodes` ou `parametres` : section des Réglages qui le remplace, `null` sinon. */
export function legacySettingsAnchor(slug: string | null | undefined): SettingsAnchor | null {
    return LEGACY_SETTINGS_TABS.find((anchor) => anchor === slug) ?? null;
}

/**
 * Ancien `?onglet=` de la fiche de campagne : un onglet connu est gardé, Périodes et Paramètres
 * ouvrent les Réglages à leur section, le reste ouvre la Vue d'ensemble.
 */
export function legacyCampaignTarget(onglet: string | null | undefined): { tab: CampaignTab; anchor: SettingsAnchor | null } {
    const anchor = legacySettingsAnchor(onglet);
    if (anchor) return { tab: 'reglages', anchor };

    return { tab: campaignTabFromSlug(onglet) ?? DEFAULT_CAMPAIGN_TAB, anchor: null };
}

/** `periodId` : période à ouvrir dans l'onglet Trames (`?periode=`), pour revenir là où l'on était. */
export function campaignPath(id: number, tab?: CampaignTab, periodId?: number | null): string {
    const base = tab ? `${PROSPECTING_PATHS.campaigns}/${id}/${tab}` : `${PROSPECTING_PATHS.campaigns}/${id}`;
    return periodId ? `${base}?periode=${periodId}` : base;
}

/** Réglages de la campagne, à une section (`#periodes`, `#parametres`, `#arguments`) ou en haut. */
export function campaignSettingsPath(id: number, anchor?: SettingsAnchor): string {
    return `${campaignPath(id, 'reglages')}${anchor ? `#${anchor}` : ''}`;
}

export function hotelPath(id: number): string {
    return `${PROSPECTING_PATHS.hotels}/${id}`;
}

export function templatePath(id: number): string {
    return `${PROSPECTING_PATHS.templates}/${id}`;
}

/** Nouvelle trame d'une période. */
export function templateNewPath(campaignId: number, periodId: number): string {
    return `${PROSPECTING_PATHS.templates}/nouvelle?campaign=${campaignId}&period=${periodId}`;
}

/** Nouvel atelier d'exemple d'une période. */
export function workshopNewPath(campaignId: number, periodId: number): string {
    return `${PROSPECTING_PATHS.workshop}/nouveau?campaign=${campaignId}&period=${periodId}`;
}

/** Relecture, filtrée sur une trame et/ou une campagne. */
export function reviewPath(filters: { trame?: number | null; campagne?: number | null } = {}): string {
    const params = new URLSearchParams();
    if (filters.trame) params.set('trame', String(filters.trame));
    if (filters.campagne) params.set('campagne', String(filters.campagne));
    const query = params.toString();
    return query ? `${PROSPECTING_PATHS.review}?${query}` : PROSPECTING_PATHS.review;
}

/** `fromTemplateId` : l'atelier a été ouvert depuis cette trame, il affiche « Retour à la trame ». */
export function workshopPath(sessionId: number, fromTemplateId?: number): string {
    const base = `${PROSPECTING_PATHS.workshop}/${sessionId}`;
    return fromTemplateId ? `${base}?${new URLSearchParams({ from: `trame:${fromTemplateId}` })}` : base;
}

/** Lit `?from=trame:{id}` de l'atelier. */
export function templateIdFromOrigin(from: string | null): number | null {
    const match = /^trame:(\d+)$/.exec(from ?? '');
    return match ? Number(match[1]) : null;
}
